import React, { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { jsPDF } from 'jspdf';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { 
  Table, 
  TableHeader, 
  TableRow, 
  TableHead, 
  TableBody, 
  TableCell 
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { 
  Gift, 
  Search, 
  Eye, 
  CheckCircle, 
  XCircle, 
  RefreshCw,
  Euro,
  Calendar,
  User,
  Mail,
  Hash,
  Clock,
  AlertTriangle,
  Plus,
  Download,
  FileText
} from 'lucide-react';
import { Label } from '@/components/ui/label';

interface Voucher {
  id: string;
  voucher_code: string;
  amount_cents: number;
  currency: string;
  buyer_name: string;
  buyer_email: string;
  recipient_name: string;
  personal_message: string | null;
  status: 'active' | 'redeemed' | 'partially_redeemed' | 'expired' | 'cancelled';
  remaining_amount_cents: number;
  redeemed_at: string | null;
  redeemed_by: string | null;
  redemption_notes: string | null;
  purchased_at: string;
  valid_until: string;
  created_at: string;
}

const statusConfig = {
  active: { label: 'Aktiv', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  redeemed: { label: 'Eingelöst', color: 'bg-gray-100 text-gray-800', icon: CheckCircle },
  partially_redeemed: { label: 'Teilweise', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
  expired: { label: 'Abgelaufen', color: 'bg-red-100 text-red-800', icon: XCircle },
  cancelled: { label: 'Storniert', color: 'bg-red-100 text-red-800', icon: XCircle },
};

// Generate a unique voucher code
function generateVoucherCode(): string {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
  let code = "KT-";
  for (let i = 0; i < 8; i++) {
    code += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return code;
}

// Load image as base64 data URL
async function loadImageAsBase64(url: string): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.drawImage(img, 0, 0);
        resolve(canvas.toDataURL('image/png'));
      } else {
        reject(new Error('Could not get canvas context'));
      }
    };
    img.onerror = () => reject(new Error('Failed to load image'));
    img.src = url;
  });
}

// Generate a beautiful PDF voucher using jsPDF with logo
async function generateVoucherPDF(data: {
  recipientName: string;
  senderName: string;
  amount: number;
  voucherCode: string;
  issueDate: string;
  validUntil: string;
  personalMessage?: string;
}): Promise<string> {
  const { recipientName, senderName, amount, voucherCode, issueDate, validUntil, personalMessage } = data;
  
  // Create landscape A5 PDF (210x148mm)
  const doc = new jsPDF({
    orientation: 'landscape',
    unit: 'mm',
    format: [210, 148]
  });

  const pageWidth = 210;
  const pageHeight = 148;

  // Colors
  const primaryColor: [number, number, number] = [30, 58, 95]; // Deep blue #1e3a5f
  const accentColor: [number, number, number] = [234, 179, 8]; // Gold #eab308
  const christmasRed: [number, number, number] = [185, 28, 28]; // Christmas red
  const christmasGreen: [number, number, number] = [22, 101, 52]; // Christmas green
  const white: [number, number, number] = [255, 255, 255];
  const warmBg: [number, number, number] = [254, 249, 240]; // Warm cream background
  const lightBg: [number, number, number] = [248, 250, 252];
  const textDark: [number, number, number] = [31, 41, 55];
  const textMuted: [number, number, number] = [107, 114, 128];

  // Background - warm cream
  doc.setFillColor(...warmBg);
  doc.rect(0, 0, pageWidth, pageHeight, 'F');
  
  // === CHRISTMAS BACKGROUND - subtle snowflakes pattern ===
  doc.setDrawColor(220, 230, 240);
  doc.setLineWidth(0.15);
  // Scattered snowflakes across background
  const snowflakePositions = [
    { x: 15, y: 60 }, { x: 45, y: 95 }, { x: 25, y: 130 },
    { x: 180, y: 55 }, { x: 195, y: 90 }, { x: 170, y: 125 },
    { x: 55, y: 45 }, { x: 155, y: 45 }, { x: 100, y: 140 },
    { x: 80, y: 75 }, { x: 130, y: 110 }
  ];
  snowflakePositions.forEach(({ x, y }) => {
    const size = 2 + Math.random() * 1.5;
    doc.line(x - size, y, x + size, y);
    doc.line(x, y - size, x, y + size);
    doc.line(x - size * 0.7, y - size * 0.7, x + size * 0.7, y + size * 0.7);
    doc.line(x - size * 0.7, y + size * 0.7, x + size * 0.7, y - size * 0.7);
  });

  // Top accent bar - dark navy like landing page
  doc.setFillColor(30, 41, 59); // Dark slate #1e293b
  doc.rect(0, 0, pageWidth, 8, 'F');

  // Header area - white/light like landing page for logo visibility
  doc.setFillColor(255, 255, 255); // White
  doc.rect(0, 8, pageWidth, 32, 'F');

  // Gold accent line under header
  doc.setFillColor(...accentColor);
  doc.rect(0, 40, pageWidth, 2, 'F');

  // Christmas decorations - snowflakes in top dark bar
  doc.setDrawColor(255, 255, 255);
  doc.setLineWidth(0.3);
  // Small snowflake 1
  const sf1X = pageWidth - 20;
  const sf1Y = 4;
  doc.line(sf1X - 2.5, sf1Y, sf1X + 2.5, sf1Y);
  doc.line(sf1X, sf1Y - 2.5, sf1X, sf1Y + 2.5);
  doc.line(sf1X - 1.8, sf1Y - 1.8, sf1X + 1.8, sf1Y + 1.8);
  doc.line(sf1X - 1.8, sf1Y + 1.8, sf1X + 1.8, sf1Y - 1.8);
  
  // Small snowflake 2
  const sf2X = pageWidth - 35;
  const sf2Y = 4;
  doc.line(sf2X - 2, sf2Y, sf2X + 2, sf2Y);
  doc.line(sf2X, sf2Y - 2, sf2X, sf2Y + 2);
  doc.line(sf2X - 1.4, sf2Y - 1.4, sf2X + 1.4, sf2Y + 1.4);
  doc.line(sf2X - 1.4, sf2Y + 1.4, sf2X + 1.4, sf2Y - 1.4);
  
  // Small snowflake 3
  const sf3X = pageWidth - 55;
  const sf3Y = 4;
  doc.line(sf3X - 2, sf3Y, sf3X + 2, sf3Y);
  doc.line(sf3X, sf3Y - 2, sf3X, sf3Y + 2);
  doc.line(sf3X - 1.4, sf3Y - 1.4, sf3X + 1.4, sf3Y + 1.4);
  doc.line(sf3X - 1.4, sf3Y + 1.4, sf3X + 1.4, sf3Y - 1.4);

  // Christmas ornaments on white header area
  // Red ornament (larger)
  doc.setFillColor(...christmasRed);
  doc.circle(pageWidth - 22, 24, 6, 'F');
  doc.setFillColor(...accentColor);
  doc.rect(pageWidth - 24.5, 17, 5, 2.5, 'F');
  doc.setDrawColor(...accentColor);
  doc.setLineWidth(0.5);
  doc.line(pageWidth - 22, 14, pageWidth - 22, 17);
  
  // Green ornament
  doc.setFillColor(...christmasGreen);
  doc.circle(pageWidth - 40, 28, 5, 'F');
  doc.setFillColor(...accentColor);
  doc.rect(pageWidth - 42, 22, 4, 2, 'F');
  doc.line(pageWidth - 40, 20, pageWidth - 40, 22);
  
  // Gold ornament (new)
  doc.setFillColor(...accentColor);
  doc.circle(pageWidth - 55, 22, 4, 'F');
  doc.setFillColor(200, 150, 50);
  doc.rect(pageWidth - 56.5, 17, 3, 1.5, 'F');
  doc.setDrawColor(200, 150, 50);
  doc.line(pageWidth - 55, 15, pageWidth - 55, 17);
  
  // Small holly leaves near ornaments
  doc.setFillColor(...christmasGreen);
  doc.ellipse(pageWidth - 65, 30, 3, 1.5, 'F');
  doc.ellipse(pageWidth - 62, 28, 3, 1.5, 'F');
  doc.setFillColor(...christmasRed);
  doc.circle(pageWidth - 63.5, 29, 1, 'F');
  doc.circle(pageWidth - 64.5, 30.5, 0.8, 'F');

  // Try to load and add logo with CORRECT aspect ratio on white background
  try {
    const logoData = await loadImageAsBase64('/images/logo-klartech.png');
    // Logo on white background - CORRECT aspect ratio (original ~3:1 ratio)
    // Using 45mm width x 15mm height for proper proportions
    doc.addImage(logoData, 'PNG', 8, 16, 45, 15);
  } catch (e) {
    console.log('Could not load logo, continuing without it');
  }

  // Header text - slogan to the right of logo (logo ends at ~53mm)
  doc.setFontSize(10);
  doc.setTextColor(...textMuted);
  doc.setFont('helvetica', 'normal');
  doc.text('IT-Hilfe & Technik-Support', 58, 22);
  doc.text('Computer · Smartphone · Tablet', 58, 28);

  // Main content card
  doc.setFillColor(...white);
  doc.roundedRect(10, 46, pageWidth - 20, 90, 3, 3, 'F');
  
  // Card border
  doc.setDrawColor(200, 200, 200);
  doc.setLineWidth(0.3);
  doc.roundedRect(10, 46, pageWidth - 20, 90, 3, 3, 'S');

  // "GESCHENKGUTSCHEIN" title with gold lines
  const titleY = 58;
  doc.setDrawColor(...accentColor);
  doc.setLineWidth(0.8);
  doc.line(25, titleY, 70, titleY);
  doc.line(pageWidth - 70, titleY, pageWidth - 25, titleY);

  doc.setTextColor(...primaryColor);
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text('GESCHENKGUTSCHEIN', pageWidth / 2, titleY + 1, { align: 'center' });

  // Amount box
  const amountY = 68;
  doc.setFillColor(...lightBg);
  doc.roundedRect(pageWidth / 2 - 35, amountY, 70, 20, 2, 2, 'F');
  
  doc.setTextColor(...primaryColor);
  doc.setFontSize(28);
  doc.setFont('helvetica', 'bold');
  doc.text(`${amount} €`, pageWidth / 2, amountY + 15, { align: 'center' });

  // Subtitle
  doc.setTextColor(...textMuted);
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.text('einlösbar für alle IT-Dienstleistungen', pageWidth / 2, amountY + 25, { align: 'center' });

  // Info section - 3 columns
  const infoY = 102;
  const colWidth = 60;
  const col1X = 20;
  const col2X = pageWidth / 2 - colWidth / 2;
  const col3X = pageWidth - 20 - colWidth;

  // Column 1 - Recipient info
  doc.setFillColor(...lightBg);
  doc.roundedRect(col1X, infoY, colWidth, 20, 2, 2, 'F');
  
  doc.setFontSize(7);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...textMuted);
  doc.text('FÜR', col1X + 4, infoY + 6);
  
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...textDark);
  doc.setFontSize(10);
  doc.text(recipientName, col1X + 4, infoY + 12);
  
  doc.setFontSize(7);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...textMuted);
  doc.text('VON', col1X + 4, infoY + 17);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...textDark);
  doc.text(senderName, col1X + 14, infoY + 17);

  // Column 2 - Voucher code (blue box)
  doc.setFillColor(...primaryColor);
  doc.roundedRect(col2X, infoY, colWidth, 20, 2, 2, 'F');
  
  doc.setTextColor(180, 190, 200);
  doc.setFontSize(7);
  doc.setFont('helvetica', 'normal');
  doc.text('GUTSCHEINCODE', col2X + colWidth / 2, infoY + 6, { align: 'center' });
  
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(13);
  doc.setFont('helvetica', 'bold');
  doc.text(voucherCode, col2X + colWidth / 2, infoY + 14, { align: 'center' });

  // Column 3 - Dates
  doc.setFillColor(...lightBg);
  doc.roundedRect(col3X, infoY, colWidth, 20, 2, 2, 'F');
  
  doc.setFontSize(7);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...textMuted);
  doc.text('AUSGESTELLT', col3X + 4, infoY + 6);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...textDark);
  doc.text(issueDate, col3X + colWidth - 4, infoY + 6, { align: 'right' });
  
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...textMuted);
  doc.text('GÜLTIG BIS', col3X + 4, infoY + 14);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(22, 163, 74); // Green
  doc.setFontSize(9);
  doc.text(validUntil, col3X + colWidth - 4, infoY + 14, { align: 'right' });

  // Personal message (if exists)
  if (personalMessage && personalMessage.trim()) {
    const msgY = 126;
    doc.setFillColor(255, 251, 235);
    doc.roundedRect(20, msgY, pageWidth - 40, 8, 1, 1, 'F');
    doc.setTextColor(146, 64, 14);
    doc.setFontSize(7);
    doc.setFont('helvetica', 'italic');
    const truncatedMessage = personalMessage.length > 100 ? personalMessage.substring(0, 97) + '...' : personalMessage;
    doc.text(`"${truncatedMessage}"`, pageWidth / 2, msgY + 5.5, { align: 'center' });
  }

  // Footer
  doc.setFillColor(...primaryColor);
  doc.rect(0, pageHeight - 10, pageWidth, 10, 'F');
  
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(7);
  doc.setFont('helvetica', 'normal');
  doc.text('www.klartech-mueritz.de  ·  Tel: +49 1512 8958006  ·  kontakt@klartech-mueritz.de', pageWidth / 2, pageHeight - 4, { align: 'center' });

  // Return as base64
  return doc.output('datauristring').split(',')[1];
}

export function VoucherManagement() {
  const [vouchers, setVouchers] = useState<Voucher[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedVoucher, setSelectedVoucher] = useState<Voucher | null>(null);
  const [redeemDialogOpen, setRedeemDialogOpen] = useState(false);
  const [redemptionAmount, setRedemptionAmount] = useState('');
  const [redemptionNotes, setRedemptionNotes] = useState('');
  const [redeemedBy, setRedeemedBy] = useState('');
  const [processing, setProcessing] = useState(false);
  
  // Create voucher dialog state
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newVoucher, setNewVoucher] = useState({
    amount: '',
    recipientName: '',
    buyerName: '',
    buyerEmail: '',
    personalMessage: '',
    validMonths: '12',
  });
  const [creating, setCreating] = useState(false);
  
  const { toast } = useToast();

  useEffect(() => {
    loadVouchers();
  }, []);

  const loadVouchers = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('gift_vouchers')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setVouchers(data || []);
    } catch (error: any) {
      console.error('Error loading vouchers:', error);
      toast({
        title: 'Fehler beim Laden',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (cents: number, currency: string = 'EUR') => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: currency,
    }).format(cents / 100);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const isExpired = (validUntil: string) => {
    return new Date(validUntil) < new Date();
  };

  const handleRedeem = async () => {
    if (!selectedVoucher) return;
    
    setProcessing(true);
    try {
      const amountToRedeem = redemptionAmount 
        ? Math.round(parseFloat(redemptionAmount.replace(',', '.')) * 100)
        : selectedVoucher.remaining_amount_cents;
      
      if (amountToRedeem > selectedVoucher.remaining_amount_cents) {
        throw new Error('Einlösebetrag übersteigt Restguthaben');
      }

      const newRemainingAmount = selectedVoucher.remaining_amount_cents - amountToRedeem;
      const newStatus = newRemainingAmount === 0 ? 'redeemed' : 'partially_redeemed';

      const { error } = await supabase
        .from('gift_vouchers')
        .update({
          status: newStatus,
          remaining_amount_cents: newRemainingAmount,
          redeemed_at: new Date().toISOString(),
          redeemed_by: redeemedBy || null,
          redemption_notes: redemptionNotes || null,
        })
        .eq('id', selectedVoucher.id);

      if (error) throw error;

      toast({
        title: 'Gutschein eingelöst',
        description: `${formatCurrency(amountToRedeem)} wurden eingelöst.`,
      });

      setRedeemDialogOpen(false);
      setSelectedVoucher(null);
      setRedemptionAmount('');
      setRedemptionNotes('');
      setRedeemedBy('');
      loadVouchers();
    } catch (error: any) {
      toast({
        title: 'Fehler',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setProcessing(false);
    }
  };

  const handleCancel = async (voucher: Voucher) => {
    try {
      const { error } = await supabase
        .from('gift_vouchers')
        .update({ status: 'cancelled' })
        .eq('id', voucher.id);

      if (error) throw error;

      toast({
        title: 'Gutschein storniert',
        description: `Gutschein ${voucher.voucher_code} wurde storniert.`,
      });

      loadVouchers();
    } catch (error: any) {
      toast({
        title: 'Fehler',
        description: error.message,
        variant: 'destructive',
      });
    }
  };

  // Create new voucher
  const handleCreateVoucher = async () => {
    if (!newVoucher.amount || !newVoucher.recipientName || !newVoucher.buyerName || !newVoucher.buyerEmail) {
      toast({
        title: 'Fehlende Angaben',
        description: 'Bitte fülle alle Pflichtfelder aus.',
        variant: 'destructive',
      });
      return;
    }

    setCreating(true);
    try {
      const amountCents = Math.round(parseFloat(newVoucher.amount.replace(',', '.')) * 100);
      if (isNaN(amountCents) || amountCents <= 0) {
        throw new Error('Ungültiger Betrag');
      }

      const voucherCode = generateVoucherCode();
      const validUntilDate = new Date();
      validUntilDate.setMonth(validUntilDate.getMonth() + parseInt(newVoucher.validMonths));
      const validUntilISO = validUntilDate.toISOString().split('T')[0];

      const { error } = await supabase
        .from('gift_vouchers')
        .insert({
          voucher_code: voucherCode,
          amount_cents: amountCents,
          remaining_amount_cents: amountCents,
          currency: 'EUR',
          buyer_name: newVoucher.buyerName,
          buyer_email: newVoucher.buyerEmail,
          recipient_name: newVoucher.recipientName,
          personal_message: newVoucher.personalMessage || null,
          status: 'active',
          valid_until: validUntilISO,
        });

      if (error) throw error;

      toast({
        title: 'Gutschein erstellt',
        description: `Gutschein ${voucherCode} über ${formatCurrency(amountCents)} wurde erstellt.`,
      });

      setCreateDialogOpen(false);
      setNewVoucher({
        amount: '',
        recipientName: '',
        buyerName: '',
        buyerEmail: '',
        personalMessage: '',
        validMonths: '12',
      });
      loadVouchers();
    } catch (error: any) {
      toast({
        title: 'Fehler',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setCreating(false);
    }
  };

  // Download voucher PDF
  const handleDownloadPDF = async (voucher: Voucher) => {
    const issueDate = new Date(voucher.purchased_at).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
    const validUntil = new Date(voucher.valid_until).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });

    try {
      const pdfBase64 = await generateVoucherPDF({
        recipientName: voucher.recipient_name,
        senderName: voucher.buyer_name,
        amount: voucher.amount_cents / 100,
        voucherCode: voucher.voucher_code,
        issueDate,
        validUntil,
        personalMessage: voucher.personal_message || undefined,
      });

      // Create download link
      const link = document.createElement('a');
      link.href = `data:application/pdf;base64,${pdfBase64}`;
      link.download = `Gutschein-${voucher.voucher_code}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast({
        title: 'PDF heruntergeladen',
        description: `Gutschein ${voucher.voucher_code} wurde als PDF gespeichert.`,
      });
    } catch (error) {
      console.error('PDF generation error:', error);
      toast({
        title: 'Fehler',
        description: 'PDF konnte nicht erstellt werden.',
        variant: 'destructive',
      });
    }
  };

  const filteredVouchers = vouchers.filter(voucher =>
    voucher.voucher_code.toLowerCase().includes(searchTerm.toLowerCase()) ||
    voucher.buyer_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    voucher.buyer_email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    voucher.recipient_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Stats
  const stats = {
    total: vouchers.length,
    active: vouchers.filter(v => v.status === 'active').length,
    redeemed: vouchers.filter(v => v.status === 'redeemed' || v.status === 'partially_redeemed').length,
    totalValue: vouchers.reduce((sum, v) => sum + v.amount_cents, 0),
    activeValue: vouchers
      .filter(v => v.status === 'active' || v.status === 'partially_redeemed')
      .reduce((sum, v) => sum + v.remaining_amount_cents, 0),
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <RefreshCw className="w-8 h-8 animate-spin mx-auto text-muted-foreground" />
          <p className="mt-4 text-muted-foreground">Gutscheine werden geladen...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Gift className="w-4 h-4" />
              Gesamt
            </div>
            <p className="text-2xl font-bold mt-1">{stats.total}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <CheckCircle className="w-4 h-4 text-green-500" />
              Aktiv
            </div>
            <p className="text-2xl font-bold mt-1">{stats.active}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Euro className="w-4 h-4" />
              Gesamtwert
            </div>
            <p className="text-2xl font-bold mt-1">{formatCurrency(stats.totalValue)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Euro className="w-4 h-4 text-green-500" />
              Offenes Guthaben
            </div>
            <p className="text-2xl font-bold mt-1 text-green-600">{formatCurrency(stats.activeValue)}</p>
          </CardContent>
        </Card>
      </div>

      {/* Main Card */}
      <Card>
        <CardHeader className="border-b bg-slate-50">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <CardTitle className="text-xl flex items-center gap-2">
              <Gift className="w-5 h-5" />
              Gutschein-Verwaltung
            </CardTitle>
            <div className="flex items-center gap-2">
              <div className="relative flex-1 sm:w-64">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Suche nach Code, Name, E-Mail..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9"
                />
              </div>
              <Button variant="outline" size="icon" onClick={loadVouchers}>
                <RefreshCw className="w-4 h-4" />
              </Button>
              <Button onClick={() => setCreateDialogOpen(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Neu
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {filteredVouchers.length === 0 ? (
            <div className="py-12 text-center text-muted-foreground">
              <Gift className="w-12 h-12 mx-auto mb-4 opacity-30" />
              <p>Keine Gutscheine gefunden</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Code</TableHead>
                    <TableHead>Betrag</TableHead>
                    <TableHead>Restguthaben</TableHead>
                    <TableHead>Empfänger</TableHead>
                    <TableHead>Käufer</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Gültig bis</TableHead>
                    <TableHead className="text-right">Aktionen</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredVouchers.map((voucher) => {
                    const statusInfo = statusConfig[voucher.status];
                    const StatusIcon = statusInfo.icon;
                    const expired = isExpired(voucher.valid_until) && voucher.status === 'active';
                    
                    return (
                      <TableRow key={voucher.id} className={expired ? 'bg-red-50' : ''}>
                        <TableCell className="font-mono font-medium">
                          {voucher.voucher_code}
                        </TableCell>
                        <TableCell>
                          {formatCurrency(voucher.amount_cents, voucher.currency)}
                        </TableCell>
                        <TableCell>
                          <span className={voucher.remaining_amount_cents < voucher.amount_cents ? 'text-yellow-600' : 'text-green-600'}>
                            {formatCurrency(voucher.remaining_amount_cents, voucher.currency)}
                          </span>
                        </TableCell>
                        <TableCell>{voucher.recipient_name}</TableCell>
                        <TableCell>
                          <div className="text-sm">
                            <div>{voucher.buyer_name}</div>
                            <div className="text-muted-foreground text-xs">{voucher.buyer_email}</div>
                          </div>
                        </TableCell>
                        <TableCell>
                          {expired ? (
                            <Badge variant="destructive" className="flex items-center gap-1 w-fit">
                              <AlertTriangle className="w-3 h-3" />
                              Abgelaufen
                            </Badge>
                          ) : (
                            <Badge className={`${statusInfo.color} flex items-center gap-1 w-fit`}>
                              <StatusIcon className="w-3 h-3" />
                              {statusInfo.label}
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <span className={expired ? 'text-red-600' : ''}>
                            {formatDate(voucher.valid_until)}
                          </span>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            {/* View Details */}
                            <Dialog>
                              <DialogTrigger asChild>
                                <Button variant="ghost" size="icon">
                                  <Eye className="w-4 h-4" />
                                </Button>
                              </DialogTrigger>
                              <DialogContent className="max-w-md">
                                <DialogHeader>
                                  <DialogTitle className="flex items-center gap-2">
                                    <Gift className="w-5 h-5" />
                                    Gutschein {voucher.voucher_code}
                                  </DialogTitle>
                                </DialogHeader>
                                <div className="space-y-4">
                                  <div className="grid grid-cols-2 gap-4">
                                    <div>
                                      <p className="text-sm text-muted-foreground flex items-center gap-1">
                                        <Euro className="w-3 h-3" /> Betrag
                                      </p>
                                      <p className="font-medium">{formatCurrency(voucher.amount_cents)}</p>
                                    </div>
                                    <div>
                                      <p className="text-sm text-muted-foreground">Restguthaben</p>
                                      <p className="font-medium text-green-600">
                                        {formatCurrency(voucher.remaining_amount_cents)}
                                      </p>
                                    </div>
                                    <div>
                                      <p className="text-sm text-muted-foreground flex items-center gap-1">
                                        <User className="w-3 h-3" /> Empfänger
                                      </p>
                                      <p className="font-medium">{voucher.recipient_name}</p>
                                    </div>
                                    <div>
                                      <p className="text-sm text-muted-foreground">Käufer</p>
                                      <p className="font-medium">{voucher.buyer_name}</p>
                                    </div>
                                    <div>
                                      <p className="text-sm text-muted-foreground flex items-center gap-1">
                                        <Mail className="w-3 h-3" /> E-Mail
                                      </p>
                                      <p className="font-medium text-sm">{voucher.buyer_email}</p>
                                    </div>
                                    <div>
                                      <p className="text-sm text-muted-foreground flex items-center gap-1">
                                        <Calendar className="w-3 h-3" /> Gekauft am
                                      </p>
                                      <p className="font-medium">{formatDateTime(voucher.purchased_at)}</p>
                                    </div>
                                    <div>
                                      <p className="text-sm text-muted-foreground">Gültig bis</p>
                                      <p className={`font-medium ${isExpired(voucher.valid_until) ? 'text-red-600' : ''}`}>
                                        {formatDate(voucher.valid_until)}
                                      </p>
                                    </div>
                                    <div>
                                      <p className="text-sm text-muted-foreground">Status</p>
                                      <Badge className={statusConfig[voucher.status].color}>
                                        {statusConfig[voucher.status].label}
                                      </Badge>
                                    </div>
                                  </div>
                                  
                                  {voucher.personal_message && (
                                    <div>
                                      <p className="text-sm text-muted-foreground mb-1">Persönliche Nachricht</p>
                                      <p className="text-sm bg-muted p-3 rounded-md italic">
                                        "{voucher.personal_message}"
                                      </p>
                                    </div>
                                  )}
                                  
                                  {voucher.redeemed_at && (
                                    <div className="border-t pt-4">
                                      <p className="text-sm font-medium mb-2">Einlösung</p>
                                      <div className="text-sm space-y-1">
                                        <p><span className="text-muted-foreground">Eingelöst am:</span> {formatDateTime(voucher.redeemed_at)}</p>
                                        {voucher.redeemed_by && <p><span className="text-muted-foreground">Von:</span> {voucher.redeemed_by}</p>}
                                        {voucher.redemption_notes && <p><span className="text-muted-foreground">Notiz:</span> {voucher.redemption_notes}</p>}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </DialogContent>
                            </Dialog>

                            {/* Download PDF */}
                            <Button 
                              variant="ghost" 
                              size="icon"
                              onClick={() => handleDownloadPDF(voucher)}
                              title="PDF herunterladen"
                            >
                              <Download className="w-4 h-4" />
                            </Button>
                            
                            {/* Redeem Button */}
                            {(voucher.status === 'active' || voucher.status === 'partially_redeemed') && !expired && (
                              <Button 
                                variant="outline" 
                                size="sm"
                                onClick={() => {
                                  setSelectedVoucher(voucher);
                                  setRedeemDialogOpen(true);
                                }}
                              >
                                <CheckCircle className="w-4 h-4 mr-1" />
                                Einlösen
                              </Button>
                            )}
                            
                            {/* Cancel Button - for active and partially redeemed vouchers */}
                            {(voucher.status === 'active' || voucher.status === 'partially_redeemed') && (
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <Button 
                                    variant="ghost" 
                                    size="icon" 
                                    className="text-red-500 hover:text-red-700 hover:bg-red-50"
                                    title="Gutschein stornieren"
                                  >
                                    <XCircle className="w-4 h-4" />
                                  </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle className="flex items-center gap-2 text-red-600">
                                      <XCircle className="w-5 h-5" />
                                      Gutschein stornieren?
                                    </AlertDialogTitle>
                                    <AlertDialogDescription asChild>
                                      <div className="space-y-3">
                                        <p>
                                          Der Gutschein <span className="font-mono font-bold">{voucher.voucher_code}</span> wird unwiderruflich storniert.
                                        </p>
                                        <div className="bg-muted p-3 rounded-md space-y-1 text-sm">
                                          <div className="flex justify-between">
                                            <span>Ursprünglicher Wert:</span>
                                            <span className="font-medium">{formatCurrency(voucher.amount_cents)}</span>
                                          </div>
                                          {voucher.status === 'partially_redeemed' && (
                                            <div className="flex justify-between text-yellow-600">
                                              <span>Verbleibendes Guthaben:</span>
                                              <span className="font-medium">{formatCurrency(voucher.remaining_amount_cents)}</span>
                                            </div>
                                          )}
                                          <div className="flex justify-between">
                                            <span>Empfänger:</span>
                                            <span className="font-medium">{voucher.recipient_name}</span>
                                          </div>
                                        </div>
                                        {voucher.status === 'partially_redeemed' && (
                                          <p className="text-yellow-600 text-sm">
                                            ⚠️ Dieser Gutschein wurde bereits teilweise eingelöst!
                                          </p>
                                        )}
                                      </div>
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                                    <AlertDialogAction 
                                      onClick={() => handleCancel(voucher)}
                                      className="bg-red-500 hover:bg-red-600"
                                    >
                                      Unwiderruflich stornieren
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Redeem Dialog */}
      <Dialog open={redeemDialogOpen} onOpenChange={setRedeemDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-500" />
              Gutschein einlösen
            </DialogTitle>
          </DialogHeader>
          {selectedVoucher && (
            <div className="space-y-4">
              <div className="bg-muted p-4 rounded-lg">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="font-mono font-bold">{selectedVoucher.voucher_code}</p>
                    <p className="text-sm text-muted-foreground">für {selectedVoucher.recipient_name}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-muted-foreground">Restguthaben</p>
                    <p className="text-xl font-bold text-green-600">
                      {formatCurrency(selectedVoucher.remaining_amount_cents)}
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="space-y-3">
                <div>
                  <label className="text-sm font-medium">
                    Einlösebetrag (leer = gesamtes Guthaben)
                  </label>
                  <div className="relative mt-1">
                    <Euro className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      type="text"
                      placeholder={`Max. ${(selectedVoucher.remaining_amount_cents / 100).toFixed(2).replace('.', ',')}`}
                      value={redemptionAmount}
                      onChange={(e) => setRedemptionAmount(e.target.value)}
                      className="pl-9"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="text-sm font-medium">Eingelöst von (Name des Kunden)</label>
                  <Input
                    placeholder="z.B. Max Mustermann"
                    value={redeemedBy}
                    onChange={(e) => setRedeemedBy(e.target.value)}
                    className="mt-1"
                  />
                </div>
                
                <div>
                  <label className="text-sm font-medium">Notizen (optional)</label>
                  <Textarea
                    placeholder="z.B. Verwendet für Smartphone-Reparatur"
                    value={redemptionNotes}
                    onChange={(e) => setRedemptionNotes(e.target.value)}
                    className="mt-1"
                    rows={2}
                  />
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setRedeemDialogOpen(false)}>
              Abbrechen
            </Button>
            <Button onClick={handleRedeem} disabled={processing}>
              {processing ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Wird eingelöst...
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Einlösen
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Voucher Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="w-5 h-5" />
              Neuen Gutschein erstellen
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label htmlFor="amount">Betrag (€) *</Label>
                <div className="relative mt-1">
                  <Euro className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="amount"
                    type="text"
                    placeholder="z.B. 50,00"
                    value={newVoucher.amount}
                    onChange={(e) => setNewVoucher({ ...newVoucher, amount: e.target.value })}
                    className="pl-9"
                  />
                </div>
              </div>
              
              <div className="col-span-2">
                <Label htmlFor="recipientName">Empfänger (Name) *</Label>
                <Input
                  id="recipientName"
                  placeholder="z.B. Oma Erika"
                  value={newVoucher.recipientName}
                  onChange={(e) => setNewVoucher({ ...newVoucher, recipientName: e.target.value })}
                  className="mt-1"
                />
              </div>
              
              <div>
                <Label htmlFor="buyerName">Käufer (Name) *</Label>
                <Input
                  id="buyerName"
                  placeholder="z.B. Max Mustermann"
                  value={newVoucher.buyerName}
                  onChange={(e) => setNewVoucher({ ...newVoucher, buyerName: e.target.value })}
                  className="mt-1"
                />
              </div>
              
              <div>
                <Label htmlFor="buyerEmail">Käufer (E-Mail) *</Label>
                <Input
                  id="buyerEmail"
                  type="email"
                  placeholder="max@example.de"
                  value={newVoucher.buyerEmail}
                  onChange={(e) => setNewVoucher({ ...newVoucher, buyerEmail: e.target.value })}
                  className="mt-1"
                />
              </div>
              
              <div className="col-span-2">
                <Label htmlFor="validMonths">Gültigkeit</Label>
                <select
                  id="validMonths"
                  value={newVoucher.validMonths}
                  onChange={(e) => setNewVoucher({ ...newVoucher, validMonths: e.target.value })}
                  className="mt-1 w-full h-10 px-3 rounded-md border border-input bg-background"
                >
                  <option value="6">6 Monate</option>
                  <option value="12">12 Monate</option>
                  <option value="18">18 Monate</option>
                  <option value="24">24 Monate</option>
                </select>
              </div>
              
              <div className="col-span-2">
                <Label htmlFor="personalMessage">Persönliche Nachricht (optional)</Label>
                <Textarea
                  id="personalMessage"
                  placeholder="z.B. Alles Gute zum Geburtstag!"
                  value={newVoucher.personalMessage}
                  onChange={(e) => setNewVoucher({ ...newVoucher, personalMessage: e.target.value })}
                  className="mt-1"
                  rows={2}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>
              Abbrechen
            </Button>
            <Button onClick={handleCreateVoucher} disabled={creating}>
              {creating ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Wird erstellt...
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4 mr-2" />
                  Erstellen
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
